/**
 * LandingHome — Yukti Landing v2, Home page.
 *
 * Faithful port of `index.html` from the AI-IDE-generated design.
 * Phase 1: static markup + visual fidelity.
 * Phase 2 will add interactivity: NEP gimmick rotating badge,
 *           hero CTA wired to /login, contact form submission, etc.
 *
 * Asset paths point to /landing-v2/assets/<filename>.webp (served from public/).
 * All SVG icons are inlined to match the original design exactly.
 */
import { Link } from 'react-router-dom';
import LandingShell from '@/components/landing/LandingShell';
import NepBadge from '@/components/landing/NepBadge';
import ContactSection from '@/components/landing/ContactSection';
import { useLandingBase } from '@/lib/landingCTAs';

export default function LandingHome() {
  const base = useLandingBase();
  return (
    <LandingShell activeNav="home">

      {/* ============== HERO ============== */}
      <section id="hero">
        <div className="container hero-grid">
          <div className="hero-text-content">
            <NepBadge />
            <h1>
              Not the Answer.<br />
              <span className="gradient-text">The Right Question.</span>
            </h1>
            <p className="hero-subtitle">
              Answers are everywhere. Understanding is rare. Yukti is the
              AI-native tool built to close that gap.
            </p>

            <div className="hero-stats">
              <div className="stat-item">
                <div className="stat-num">3x</div>
                <div className="stat-label">Better Retention</div>
              </div>
              <div className="stat-divider" />
              <div className="stat-item">
                <div className="stat-num">NEP</div>
                <div className="stat-label">2020 Aligned</div>
              </div>
              <div className="stat-divider" />
              <div className="stat-item">
                <div className="stat-num">Class</div>
                <div className="stat-label">6 through 12</div>
              </div>
            </div>
          </div>

          <div className="hero-visual">
            <div className="hero-card-glow" />
            <div className="hero-collage-container">
              <div className="collage-bg-grid" />
              <div className="collage-stripe stripe-orange-long" />
              <div className="collage-stripe stripe-orange-short" />
              <div className="collage-stripe stripe-red" />
              <div className="collage-dots">
                <span /><span /><span /><span />
              </div>
              <img
                src="/landing-v2/assets/confident_student.webp"
                alt="Indian student learning Socratic reasoning using Yukti"
                className="collage-student-img"
              />
              <div className="collage-prompt-card glass">
                <div className="prompt-icon">Y</div>
                <div className="prompt-text">
                  What would happen if gravity reversed for 1 second?
                </div>
              </div>
              <div className="collage-stat-block">
                <div className="stat-block-val">3x</div>
                <div className="stat-block-lbl">Deeper Recall</div>
              </div>
            </div>
          </div>
        </div>

        <div className="hero-trust-divider container">
          <p className="trust-title">
            <strong className="trust-title-bold">
              What if every &ldquo;I don&rsquo;t get it&rdquo; became a breakthrough?
            </strong>
            {' '}For every learner, across every board in India.
          </p>
        </div>
      </section>

      {/* ============== PHILOSOPHY ============== */}
      <section className="section-padding light-bg">
        <div className="container">
          <div className="section-header text-center">
            <span className="section-tag">THE APPROACH</span>
            <h2>Learning That Lasts</h2>
            <p className="section-subtitle" style={{ maxWidth: 600, margin: '0 auto' }}>
              Decades of cognitive research confirm: students who arrive at
              answers through their own reasoning retain knowledge 3x longer.
              Yukti is built entirely on this principle.
            </p>
          </div>

          <div className="philosophy-grid four-cols">
            <div className="philosophy-card text-center">
              <div className="ph-icon-box bg-purple" style={{ margin: '0 auto 16px auto' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" /><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" /><line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
              </div>
              <h3>Question First</h3>
              <p>The AI never gives an answer directly. It guides students to reason their way there — building understanding that holds under pressure.</p>
            </div>

            <div className="philosophy-card text-center">
              <div className="ph-icon-box bg-teal" style={{ margin: '0 auto 16px auto' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
                </svg>
              </div>
              <h3>Moves at Your Pace</h3>
              <p>Every session reads how a student thinks and adjusts in real time — matching their speed, their style. No two learning paths look the same.</p>
            </div>

            <div className="philosophy-card text-center">
              <div className="ph-icon-box bg-gold" style={{ margin: '0 auto 16px auto' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
              </div>
              <h3>One Ecosystem</h3>
              <p>Student learning, parent oversight, and school diagnostics — all connected in a single real-time loop. No information lost between home and classroom.</p>
            </div>

            <div className="philosophy-card text-center">
              <div className="ph-icon-box bg-rose" style={{ margin: '0 auto 16px auto' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                </svg>
              </div>
              <h3>No Pressure. No Rush.</h3>
              <p>There are no deadlines inside Yukti. No wrong answers that shame. No race to finish. Just a calm, patient space where curiosity is always welcome.</p>
            </div>
          </div>

          <div className="boards-logo-flex">
            <div className="board-badge">CBSE</div>
            <div className="board-badge">ICSE</div>
            <div className="board-badge">State Boards</div>
            <div className="board-badge">NEP 2020</div>
          </div>
        </div>
      </section>

      {/* ============== WHO IS IT FOR ============== */}
      <section className="section-padding">
        <div className="container">
          <div className="section-header text-center">
            <span className="section-tag">CHOOSE YOUR PORTAL</span>
            <h2>Made for Every Stakeholder</h2>
          </div>

          <div className="philosophy-grid">
            <Link
              to={`${base}/student`}
              className="philosophy-card text-center"
              style={{ cursor: 'pointer', textDecoration: 'none', color: 'inherit' }}
              data-testid="portal-card-student"
            >
              <img
                src="/landing-v2/assets/confident_student.webp"
                alt="Student"
                style={{ width: '100%', height: 200, objectFit: 'cover', borderRadius: 12, marginBottom: 16, boxShadow: '0 8px 24px rgba(0,0,0,0.1)' }}
              />
              <h3>Student</h3>
              <p>AI mentor that talks <em>with</em> you — type or speak — in the language you think in. Adaptive quizzes. Syllabus navigator. Everything a curious learner needs.</p>
              <span
                className="btn btn-text"
                style={{ color: 'var(--color-purple)', fontWeight: 700, marginTop: 16, display: 'inline-block' }}
              >
                Explore &rarr;
              </span>
            </Link>

            <Link
              to={`${base}/parent`}
              className="philosophy-card text-center"
              style={{ cursor: 'pointer', textDecoration: 'none', color: 'inherit' }}
              data-testid="portal-card-parent"
            >
              <img
                src="/landing-v2/assets/confident_parent.webp"
                alt="Parent"
                style={{ width: '100%', height: 200, objectFit: 'cover', borderRadius: 12, marginBottom: 16, boxShadow: '0 8px 24px rgba(0,0,0,0.1)' }}
              />
              <h3>Parent</h3>
              <p>Daily concept scores. Chat transcripts. Know what your child understands — before exam day.</p>
              <span
                className="btn btn-text"
                style={{ color: 'var(--color-teal)', fontWeight: 700, marginTop: 16, display: 'inline-block' }}
              >
                Explore &rarr;
              </span>
            </Link>

            <Link
              to={`${base}/school`}
              className="philosophy-card text-center"
              style={{ cursor: 'pointer', textDecoration: 'none', color: 'inherit' }}
              data-testid="portal-card-school"
            >
              <img
                src="/landing-v2/assets/confident_teacher.webp"
                alt="Teacher"
                style={{ width: '100%', height: 200, objectFit: 'cover', borderRadius: 12, marginBottom: 16, boxShadow: '0 8px 24px rgba(0,0,0,0.1)' }}
              />
              <h3>School</h3>
              <p>Live heatmaps. One-click assessments. See every student&apos;s gaps before they widen.</p>
              <span
                className="btn btn-text"
                style={{ color: 'var(--color-gold)', fontWeight: 700, marginTop: 16, display: 'inline-block' }}
              >
                Explore &rarr;
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* ============== LANGUAGE & MODALITY ============== */}
      <section className="lang-strip-section">
        <div className="container">
          <div className="lang-strip-inner">
            <div className="lang-strip-headline">
              <span className="section-tag">Built for Bharat</span>
              <h2 className="lang-strip-title">
                Speaks your language.<br />
                <span className="gradient-text">Hears your voice.</span>
              </h2>
              <p className="lang-strip-sub">
                Ask a question by typing or just speaking aloud — Yukti
                understands both. Whether you think in Hindi, Tamil, Bengali, or
                English, the conversation happens in the language that feels
                natural to you.
              </p>
            </div>
            <div className="lang-strip-pills">
              <div className="lang-modality-card">
                <div className="lm-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                  </svg>
                </div>
                <div className="lm-label">Text Chat</div>
                <div className="lm-desc">Type your question, anytime</div>
              </div>
              <div className="lang-modality-card">
                <div className="lm-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" /><path d="M19 10v2a7 7 0 0 1-14 0v-2" /><line x1="12" y1="19" x2="12" y2="23" /><line x1="8" y1="23" x2="16" y2="23" />
                  </svg>
                </div>
                <div className="lm-label">Voice</div>
                <div className="lm-desc">Just speak — Yukti listens</div>
              </div>
              <div className="lang-modality-card">
                <div className="lm-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" /><line x1="2" y1="12" x2="22" y2="12" /><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                  </svg>
                </div>
                <div className="lm-label">Multilingual</div>
                <div className="lm-desc">Hindi, Tamil, Bengali, English &amp; more</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============== CONTACT ============== */}
      <ContactSection sourcePage="home" />

    </LandingShell>
  );
}
